<template>
  <div class="event-wizard m-x-auto" style="max-width: 560px;">
    <!-- In the app embed the native nav bar already titles the screen -->
    <div v-if="!forAppEmbed" class="m-b-m">
      <h1 class="font-heavy m-y-none">New Event</h1>
    </div>
    <!-- The per-step Back buttons live below the fold on long steps, so the
         header carries an always-visible one; without it the only on-screen
         back affordance is the native chevron, which exits the whole wizard -->
    <div v-if="onNumberedStep" class="flex align-items-center justify-space-between m-b-m">
      <span class="flex gap-x-xxs">
        <span v-for="dot in 4" :key="dot" class="rounded-full"
              :style="{ height: '6px', width: dot === stepNumber ? '24px' : '12px' }" :class="dot <= stepNumber ? 'bg-green-500' : 'bg-gray-200'"></span>
      </span>
      <button type="button" class="btn btn-link p-x-n p-y-n text-14 text-gray-500" data-cy="wizard-header-back"
              @click="goBack(previousStep)">&larr; Back</button>
    </div>

    <!-- ====================== HOME: start fresh or reuse ====================== -->
    <div v-if="step === 'home'">
      <p v-if="error" class="text-14 text-red-600 m-b-sm" data-cy="wizard-home-error">{{ errorMessage }}</p>
      <div v-if="calendarSlotHint" data-cy="wizard-calendar-hint"
           class="flex align-items-start gap-x-sm bg-blue-50 border border-solid border-blue-200 rounded-xxs p-sm m-b-sm">
        <i class="fa-solid fa-calendar-days text-blue-600" style="margin-top: 2px;"></i>
        <span class="text-14 text-blue-700">
          <span class="font-semi">From your selection:</span> {{ calendarSlotHint }}
          <span class="block text-12 text-blue-600 m-t-xxs">We'll start with this date &amp; time &mdash; reuse an event below or create one.</span>
        </span>
      </div>
      <button type="button" :disabled="loading" @click="startFreshEvent" data-cy="wizard-new-event"
              class="wizard-primary-cta w-full text-left rounded-xxs border-none p-y-sm p-x-m cursor-pointer m-b-sm bg-green-500">
        <span class="flex align-items-center justify-space-between">
          <span>
            <span class="block text-14 font-semi" style="color: #fff;">Create event</span>
            <span class="block text-14 m-t-xxs" style="color: rgba(255,255,255,0.75);">Build one step by step from scratch</span>
          </span>
          <i class="fa-solid fa-chevron-right" style="color: rgba(255,255,255,0.75);"></i>
        </span>
      </button>
      <template v-if="templates.length > 0">
        <div class="flex align-items-center gap-x-sm p-t-xs m-b-xxs">
          <span class="flex-1 bg-gray-200" style="height: 1px;"></span>
          <span class="upper-label font-semi text-gray-400">Or reuse a recent event</span>
          <span class="flex-1 bg-gray-200" style="height: 1px;"></span>
        </div>
        <p class="text-center text-12 text-gray-500 m-b-sm">
          Keeps the location, roster &amp; details &mdash; <span class="font-semi text-gray-700">you'll set the new date &amp; time</span>
        </p>
        <button v-for="tpl in templates" :key="`${tpl.name}-${tpl.event_type}`" type="button" :disabled="loading" @click="useTemplate(tpl)"
                data-cy="wizard-template-card"
                class="wizard-template-card w-full text-left rounded-xxs border border-solid border-gray-200 bg-white p-m cursor-pointer m-b-sm">
          <span class="flex flex-wrap justify-space-between align-items-center gap-x-sm gap-y-xxs">
            <span class="text-16 font-semi text-gray-900">{{ tpl.name }}</span>
            <span class="inline-flex align-items-center gap-x-xxs upper-label text-gray-500 bg-gray-50 border border-solid border-gray-200 rounded-full p-x-xs p-y-xxs">
              <span v-if="tpl.color" :style="{ 'background-color': tpl.color, width: '8px', height: '8px', 'border-radius': '50%', display: 'inline-block' }"></span>
              {{ templateTypeLabel(tpl) }}
            </span>
          </span>
          <span v-if="tpl.location_name" class="block text-14 text-gray-600 m-t-xxs">
            <i class="fa-solid fa-location-dot text-gray-400 m-r-xxs"></i>{{ tpl.location_name }}
          </span>
          <span v-if="tpl.rosters.length > 0" class="m-t-xs inline-flex align-items-center gap-x-xxs text-12 font-semi text-green-700 bg-green-50 rounded-full p-x-sm p-y-xxs">
            &#10003; For: {{ templateRosterSummary(tpl) }}
          </span>
          <span v-else class="m-t-xs inline-flex align-items-center gap-x-xxs text-12 font-semi text-gray-600 bg-gray-50 rounded-full p-x-sm p-y-xxs">
            For: the whole team
          </span>
        </button>
      </template>
    </div>

    <!-- ====================== STEP 1: what & when ====================== -->
    <div v-if="step === '1'">
      <h2 class="text-16 font-semi m-t-none m-b-xxs">What &amp; when</h2>
      <p v-if="cameFromTemplate && selectedTemplate" class="text-14 text-gray-500 m-b-m">
        Prefilled from <span class="font-semi">{{ selectedTemplate.name }}</span> &mdash; now pick the date &amp; time.
      </p>
      <p v-else class="text-14 text-gray-500 m-b-m">The basics &mdash; everything else comes after.</p>
      <div class="form-group">
        <label>Name</label>
        <input v-model="eventName" type="text" class="form-control" data-cy="wizard-name">
      </div>
      <div class="form-group">
        <label>Event Type</label>
        <select v-model="eventType" class="form-control" data-cy="wizard-event-type">
          <option value="practice">Practice</option>
          <option value="dual_meet">Dual Meet</option>
          <option value="scramble">Scramble</option>
          <option value="tournament">Tournament</option>
          <option value="other">Other</option>
        </select>
        <div v-if="looksLikePractice" class="m-t-xs p-sm rounded-xs bg-yellow-50 border border-solid border-yellow-200" data-cy="wizard-practice-hint">
          <p class="text-14 text-yellow-800 m-b-xxs"><span class="font-semi">Is this a practice?</span> "Other" events without a roster show up on every family's schedule. Make it a Practice and pick the rosters it's for, so each family only sees their own kid's practices.</p>
          <button type="button" class="btn btn-link p-x-n p-y-n text-14 font-semi" @click="switchToPractice" data-cy="wizard-switch-to-practice">Make it a Practice</button>
        </div>
      </div>
      <div class="form-group">
        <label>Location</label>
        <location-select
            :key="formStateKey"
            :initial-location-id="locationId"
            :initial-location="locationFreeText"
            @change="locationChanged"
        ></location-select>
      </div>
      <div class="form-group">
        <label>Date &amp; Time</label>
        <date-time-range
            :key="formStateKey"
            :initial-start-date="startDateStr"
            :initial-start-time="startTimeStr"
            :initial-end-date="endDateStr"
            :initial-end-time="endTimeStr"
            :initial-all-day="allDay"
            :default-to-today="!cameFromTemplate"
            :time-zone="eventFormZone"
            @change="dateRangeChanged"
        ></date-time-range>
        <time-zone-picker
            v-if="!allDay"
            v-model="timeZoneOverride"
            :location-time-zone="eventTimeZone"
            :at="eventStart"
        ></time-zone-picker>
      </div>
      <div v-if="repeatable" class="form-group">
        <label class="block">Repeat</label>
        <div class="btn-group segmented-control m-b-sm" role="group" aria-label="Repeat">
          <button type="button" class="btn btn-default btn-compact" :class="{ active: repeatEnabled }"
                  @click="setRepeatEnabled(true)" data-cy="wizard-repeat-yes">Yes</button>
          <button type="button" class="btn btn-default btn-compact" :class="{ active: !repeatEnabled }"
                  @click="setRepeatEnabled(false)" data-cy="wizard-repeat-no">No</button>
        </div>
        <div v-if="repeatEnabled">
          <label class="text-12 m-y-none tracking-wide sr-only">Repeats on</label>
          <div class="flex gap-x-xxs m-t-xxs">
            <button v-for="day in weekdayOptions" :key="day.key" type="button"
                    @click="toggleWeekday(day.key)"
                    class="rounded-full border border-solid text-12 font-semi cursor-pointer"
                    :class="repeatDays[day.key] ? 'border-green-700 text-green-50 bg-green-700' : 'border-gray-300 text-gray-600 bg-white'"
                    :data-cy="`wizard-repeat-${day.key}`"
                    style="width: 36px; height: 36px;">
              {{ day.label }}
            </button>
          </div>
          <div v-if="anyRepeatDays" class="m-t-sm">
            <label>Until</label>
            <input v-model="repeatUntil" type="date" class="form-control" data-cy="wizard-repeat-until">
            <p v-if="repeatUntilInvalid" class="text-14 text-red-600 m-t-xxs m-b-none" data-cy="wizard-repeat-until-error">
              The repeat-until date must be after the start date.
            </p>
          </div>
          <p v-if="repeatSummary && !repeatUntilInvalid" class="text-14 text-gray-500 m-t-xs m-b-none" data-cy="wizard-repeat-summary">{{ repeatSummary }}</p>
        </div>
      </div>
      <div v-if="eventType === 'dual_meet'" class="form-group">
        <label>Opposing Team</label>
        <div v-if="opposingTeam">
          <div class="flex align-items-center gap-x-sm">
            <span class="text-14 font-semi">{{ opposingTeam.name }}</span>
            <a class="cursor-pointer text-14" @click="opposingTeam = null">Change...</a>
          </div>
        </div>
        <search v-show="!opposingTeam"
                readable-model-name="opposing team"
                :allow-add="true"
                only-dismiss-model="OpposingTeam"
                class="search-form"
                response-key="teams"
                :mutate-history="false"
                :small-input="true"
                placeholder="Type opposing team name..."
                base-url="/api/v1/opposing_teams">
          <template v-slot:list="slotProps">
            <div class="owrestler-oteam-wrapper" @click="opposingTeam = slotProps.result">
              <opposing-team-search-result
                  v-bind:result="slotProps.result"
                  v-bind:is-selectable="true"
                  v-bind:key="slotProps.result.id"
              >
              </opposing-team-search-result>
            </div>
          </template>
          <template v-slot:addform="slotProps">
            <opposing-team-create v-bind:name="slotProps.eventName"></opposing-team-create>
          </template>
        </search>
      </div>
      <p v-if="error" class="text-14 text-red-600">{{ errorMessage }}</p>
      <button type="button" class="btn btn-primary w-full" @click="nextFromBasics" data-cy="wizard-next-1">{{ isPractice ? 'Next: Who can check in?' : 'Next: Who is this for?' }}</button>
      <button type="button" class="btn btn-white w-full m-t-sm" @click="goBack('home')">&larr; Back</button>
    </div>

    <!-- ====================== STEP 2: who can check in ====================== -->
    <div v-if="step === '2'">
      <h2 class="text-16 font-semi m-t-none m-b-xxs">{{ isPractice ? 'Who can check in?' : 'Who is this event for?' }}</h2>
      <p v-if="isPractice" class="text-14 text-gray-500 m-b-m">This creates the attendance list for this event.</p>
      <p v-else class="text-14 text-gray-500 m-b-m">Linked rosters see this as their event — and creates the attendance list too.</p>
      <check-in-access
          :event-type="eventType"
          :mode="checkInMode"
          :roster-events="rosterEvents"
          @set-mode="setCheckInMode"
          @toggle-roster="toggleRoster"
      ></check-in-access>
      <p v-if="error" class="text-14 text-red-600">{{ errorMessage }}</p>
      <div class="m-t-m">
        <button type="button" class="btn btn-primary w-full" @click="nextFromCheckIn" data-cy="wizard-next-2">Next: Extras</button>
        <button type="button" class="btn btn-white w-full m-t-sm" @click="goBack('1')">&larr; Back</button>
      </div>
    </div>

    <!-- ====================== STEP 3: extras (v-show keeps trix alive) ====================== -->
    <div v-show="step === '3'">
      <h2 class="text-16 font-semi m-t-none m-b-xxs">Extras <span class="text-14 font-normal text-gray-400">&mdash; all optional</span></h2>
      <p class="text-14 text-gray-500 m-b-m">Skip these and the event still works.</p>
      <div class="form-group">
        <label>Color</label>
        <div class="flex flex-wrap align-items-center gap-xs m-t-xxs">
          <button v-for="cOption in colorOptions" :key="cOption" type="button"
                  class="border-none cursor-pointer rounded-full"
                  :style="{
                    'background-color': cOption,
                    width: '24px',
                    height: '24px',
                    padding: 0,
                    'box-shadow': isActiveColor(cOption) ? `0 0 0 2px #fff, 0 0 0 4px ${cOption}` : 'none',
                  }"
                  :aria-label="`Use color ${cOption}`"
                  @click="eventColor = cOption">
          </button>
          <!-- The custom "swatch": a rainbow circle with the native color input
               invisibly overlaid, so it reads as one more color choice -->
          <label class="cursor-pointer rounded-full m-b-none relative" title="Custom color"
                 :style="{
                   width: '24px',
                   height: '24px',
                   background: 'conic-gradient(#D64345, #FFC978, #52B788, #23c6c8, #4299d7, #7C6DE8, #D64345)',
                   'box-shadow': customColorActive ? `0 0 0 2px #fff, 0 0 0 4px ${eventColor}` : 'none',
                 }">
            <input
                type="color"
                :value="eventColor || '#27aa83'"
                @input="eventColor = $event.target.value"
                aria-label="Custom color"
                class="cursor-pointer absolute"
                style="top: 0; left: 0; width: 100%; height: 100%; opacity: 0; border: none; padding: 0;"
            >
          </label>
        </div>
        <p class="text-12 text-gray-400 m-t-xxs m-b-none">Tap the rainbow dot for custom colors.</p>
      </div>
      <div v-if="paidSessionTagging" class="form-group">
        <label>Registration</label>
        <select v-model="paidSession" class="form-control" data-cy="wizard-paid-session">
          <option :value="null">None</option>
          <option v-for="session in paidSessions" :key="session.id" :value="session">{{ session.name }}</option>
        </select>
        <p class="text-12 text-gray-400 m-t-xxs">Adds a sign-up button on the event to your public calendar.</p>
      </div>
      <div class="form-group">
        <label>Notes</label>
        <input id="wizard-notes" type="hidden" name="content">
        <trix-editor input="wizard-notes" class="trix-content"></trix-editor>
      </div>
      <div class="m-t-m">
        <button type="button" class="btn btn-primary w-full" @click="advanceTo('4')" data-cy="wizard-next-3">Next: Review</button>
        <button type="button" class="btn btn-white w-full m-t-sm" @click="goBack('2')">&larr; Back</button>
      </div>
    </div>

    <!-- ====================== STEP 4: review ====================== -->
    <div v-if="step === '4'">
      <h2 class="text-16 font-semi m-t-none m-b-m">Review</h2>
      <div class="rounded-xxs border border-solid border-gray-200">
        <div class="p-sm flex justify-space-between align-items-center gap-x-sm">
          <div>
            <p class="text-12 text-gray-400 m-b-none">{{ readableEventType }}</p>
            <p class="text-14 font-semi text-gray-900 m-b-none flex align-items-center gap-x-xs" data-cy="wizard-review-name">
              <span v-if="eventColor" :style="{ 'background-color': eventColor, width: '10px', height: '10px', 'border-radius': '50%', display: 'inline-block', 'flex-shrink': 0 }"
                    :title="`Color: ${eventColor}`"></span>
              <span>{{ eventName }}</span>
            </p>
          </div>
          <button type="button" class="btn btn-link p-x-n text-14 font-semi cursor-pointer text-green-600 flex-shrink-0" @click="advanceTo('1')">Edit</button>
        </div>
        <div class="p-sm flex justify-space-between align-items-start gap-x-sm border-top">
          <div>
            <p class="text-12 text-gray-400 m-b-none">When</p>
            <p class="text-14 font-semi text-gray-900 m-b-none" data-cy="wizard-review-when">{{ reviewWhen }}</p>
            <template v-if="isRepeating">
              <p class="text-12 text-gray-400 m-b-none m-t-sm">Repeat</p>
              <div class="flex gap-x-xxs m-t-xxs" data-cy="wizard-review-repeat">
                <span v-for="day in weekdayOptions" :key="day.key"
                      class="rounded-full inline-flex align-items-center justify-center text-12 font-semi"
                      :class="repeatDays[day.key] ? 'text-green-50 bg-green-700' : 'text-gray-400 bg-gray-100'"
                      style="width: 24px; height: 24px;">
                  {{ day.label }}
                </span>
              </div>
              <p class="text-12 text-gray-400 m-b-none m-t-sm">Until</p>
              <p class="text-14 font-semi text-gray-900 m-b-none" data-cy="wizard-review-repeat-until">{{ reviewRepeatUntil }}</p>
            </template>
          </div>
          <button type="button" class="btn btn-link p-x-n text-14 font-semi cursor-pointer text-green-600 flex-shrink-0" @click="advanceTo('1')">Edit</button>
        </div>
        <div class="p-sm flex justify-space-between align-items-center gap-x-sm border-top">
          <div>
            <p class="text-12 text-gray-400 m-b-none">Location</p>
            <p v-if="reviewLocation" class="text-14 font-semi text-gray-900 m-b-none" data-cy="wizard-review-location">{{ reviewLocation }}</p>
            <p v-else class="text-14 text-gray-400 m-b-none" data-cy="wizard-review-location">No location set</p>
          </div>
          <button type="button" class="btn btn-link p-x-n text-14 font-semi cursor-pointer text-green-600 flex-shrink-0" @click="advanceTo('1')">Edit</button>
        </div>
        <div v-if="eventType === 'dual_meet'" class="p-sm flex justify-space-between align-items-center gap-x-sm border-top">
          <div>
            <p class="text-12 text-gray-400 m-b-none">Opposing team</p>
            <p v-if="opposingTeam" class="text-14 font-semi text-gray-900 m-b-none" data-cy="wizard-review-opposing">{{ opposingTeam.name }}</p>
            <p v-else class="text-14 text-gray-400 m-b-none" data-cy="wizard-review-opposing">Not set</p>
          </div>
          <button type="button" class="btn btn-link p-x-n text-14 font-semi cursor-pointer text-green-600 flex-shrink-0" @click="advanceTo('1')">Edit</button>
        </div>
        <div class="p-sm flex justify-space-between align-items-center gap-x-sm border-top">
          <div>
            <p class="text-12 text-gray-400 m-b-none">{{ accessLabel }}</p>
            <p class="text-14 font-semi text-gray-900 m-b-none" data-cy="wizard-review-access">{{ reviewAccess.message }}</p>
          </div>
          <button type="button" class="btn btn-link p-x-n text-14 font-semi cursor-pointer text-green-600 flex-shrink-0" @click="advanceTo('2')">Edit</button>
        </div>
        <div v-if="paidSessionTagging" class="p-sm flex justify-space-between align-items-center gap-x-sm border-top">
          <div>
            <p class="text-12 text-gray-400 m-b-none">Registration</p>
            <p v-if="paidSession" class="text-14 font-semi text-gray-900 m-b-none" data-cy="wizard-review-registration">{{ paidSession.name }}</p>
            <p v-else class="text-14 text-gray-400 m-b-none" data-cy="wizard-review-registration">None</p>
          </div>
          <button type="button" class="btn btn-link p-x-n text-14 font-semi cursor-pointer text-green-600 flex-shrink-0" @click="advanceTo('3')">Edit</button>
        </div>
        <div v-if="hasReviewNotes" class="p-sm border-top" data-cy="wizard-review-notes">
          <div class="flex justify-space-between align-items-center gap-x-sm">
            <div>
              <p class="text-12 text-gray-400 m-b-none">Notes</p>
              <a class="text-14 font-semi cursor-pointer text-green-600" @click="showReviewNotes = !showReviewNotes" data-cy="wizard-review-notes-toggle">
                {{ showReviewNotes ? 'Hide notes' : 'Show notes' }}
              </a>
            </div>
            <button type="button" class="btn btn-link p-x-n text-14 font-semi cursor-pointer text-green-600 flex-shrink-0" @click="advanceTo('3')">Edit</button>
          </div>
          <div v-show="showReviewNotes" class="trix-content text-14 text-gray-900 m-t-xs" v-html="reviewNotesHtml"></div>
        </div>
      </div>
      <p v-if="error" class="text-14 text-red-600 m-t-sm">{{ errorMessage }}</p>
      <div class="m-t-m">
        <ladda-button @lbClicked="postEvent" el-class="btn-primary w-full" :loading="loading">
          Create Event
        </ladda-button>
      </div>
      <button type="button" class="btn btn-white w-full m-t-sm" @click="goBack('3')">Back</button>
    </div>
  </div>
</template>

<script>
import Search from '../../shared/search.vue';
import LaddaButton from '../../shared/ladda_button.vue';
import OpposingTeamSearchResult from './opposing_team_search_result.vue';
import OpposingTeamCreate from './opposing_team_create.vue';
import CheckInAccess from './check_in_access.vue';
import DateTimeRange from './date_time_range.vue';
import LocationSelect from './location_select.vue';
import TimeZonePicker from './time_zone_picker.vue';
import { plainTextFromTrix } from '../trix_text';
import { formTimeZone, differsFromBrowser } from '../event_time_zone';
import { trixFileAttachmentMix } from './mixins/trix_file_attachment_mix';
import { appEmbedMix } from './mixins/app_embed_mix';

// Guided event creation wizard (WRE-667). Replaces the old create-event modal
// with a template-first flow: reuse a recent event, or build one in four steps
// (what & when -> who can check in -> extras -> review). Practices must link at
// least one roster; the API enforces the same rule on create.
export default {
  name: 'event-wizard',
  components: {
    Search,
    LaddaButton,
    OpposingTeamSearchResult,
    OpposingTeamCreate,
    CheckInAccess,
    DateTimeRange,
    LocationSelect,
    TimeZonePicker,
  },
  mixins: [trixFileAttachmentMix, appEmbedMix],
  props: {
    paidSessionTagging: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      step: 'home',
      // Embed-only stand-in for browser history: advanceTo records the step
      // we came from so Back retraces actual visits (review -> Edit -> Back
      // returns to review), matching what history.back() gives the web.
      stepHistory: [],
      loading: false,
      error: false,
      errorMessage: '',

      templates: [],
      selectedTemplate: null,
      cameFromTemplate: false,

      eventName: '',
      eventType: 'practice',
      eventColor: '#27aa83',

      // Raw field state for date-time-range; the component is re-mounted (via
      // :key) whenever a template rewrites these so the new initial props apply.
      startDateStr: '',
      startTimeStr: '',
      endDateStr: '',
      endTimeStr: '',
      allDay: false,
      formStateKey: 0,
      // Parsed range from date-time-range's change event
      eventStart: null,
      eventEnd: null,
      dateRangeValid: false,

      // Yes/No toggle gating the repeat pills. Off by default; No clears any
      // picked days so anyRepeatDays stays the single source of "is repeating".
      repeatEnabled: false,
      repeatDays: {
        sun: false, mon: false, tue: false, wed: false, thu: false, fri: false, sat: false,
      },
      repeatUntil: '',

      checkInMode: 'rosters',
      rosterEvents: {},

      locationId: null,
      locationFreeText: '',
      // What location-select last emitted; goes into the payload
      payloadLocationId: null,
      payloadLocation: '',
      // Display-only name of a picked structured location (the payload carries
      // just the id), so the review screen can show it
      payloadLocationName: '',
      // The picked location's own IANA zone (null = follows the team)
      eventTimeZone: null,
      // Explicit per-event zone (away tournaments); wins over the location's
      timeZoneOverride: null,
      // Location id eventTimeZone was resolved for (null = no location). When
      // it doesn't match the picked location, the zone is unknown (list still
      // loading, or it failed to load).
      zoneResolvedForLocationId: null,

      paidSessions: [],
      paidSession: null,
      pendingPaidSessionId: null,

      eventNotes: '',
      trixEditor: null,
      // Snapshot of the notes HTML taken when we land on the review step, so the
      // review can render/expand it without re-reading trix on every keystroke
      reviewNotesHtml: '',
      showReviewNotes: false,

      opposingTeam: null,

      colorOptions: [
        '#27aa83',
        '#4299d7',
        '#663dae',
        '#c8992d',
        '#003E6B',
        '#D64345',
        '#FFC978',
        '#7C6DE8',
        '#147B63',
        '#23c6c8',
        '#6C757D', // Slate Gray
        '#2D5BA8', // Royal Blue
        '#FF8C42', // Tangerine
        '#52B788', // Mint Green
        '#8B5A8C', // Plum Purple
        '#A8312F', // Burgundy
        '#000000',
      ],
      weekdayOptions: [
        {
          key: 'sun', label: 'S', long: 'Sun', wday: 0,
        },
        {
          key: 'mon', label: 'M', long: 'Mon', wday: 1,
        },
        {
          key: 'tue', label: 'T', long: 'Tue', wday: 2,
        },
        {
          key: 'wed', label: 'W', long: 'Wed', wday: 3,
        },
        {
          key: 'thu', label: 'T', long: 'Thu', wday: 4,
        },
        {
          key: 'fri', label: 'F', long: 'Fri', wday: 5,
        },
        {
          key: 'sat', label: 'S', long: 'Sat', wday: 6,
        },
      ],
    };
  },
  computed: {
    // Clock the date fields are typed on: override, else the location's zone;
    // null means the browser's
    eventFormZone() {
      return this.timeZoneOverride || this.eventTimeZone;
    },
    // Timed events can't be created until we know which clock the typed times
    // are on (an override already pins it; all-day dates have none)
    zoneUnknown() {
      return !this.allDay && !this.timeZoneOverride && this.zoneResolvedForLocationId !== this.payloadLocationId;
    },
    onNumberedStep() {
      return ['1', '2', '3', '4'].includes(this.step);
    },
    stepNumber() {
      return this.onNumberedStep ? parseInt(this.step, 10) : 0;
    },
    // Where the header's Back lands: one step earlier, mirroring the per-step
    // Back buttons at the bottom of each step.
    previousStep() {
      return this.stepNumber <= 1 ? 'home' : String(this.stepNumber - 1);
    },
    repeatable() {
      return this.eventType === 'practice' || this.eventType === 'other';
    },
    anyRepeatDays() {
      return _.some(_.values(this.repeatDays));
    },
    isPractice() {
      return this.eventType === 'practice';
    },
    // Coaches dodge the practice roster requirement by filing practices as
    // "Other" with no roster, which floods every family's "My kids" schedule.
    // Nudge (don't block) when the name gives it away.
    looksLikePractice() {
      return this.eventType === 'other' && /\bpractices?\b/i.test(this.eventName || '');
    },
    // "Check-in access" is the honest label for practices; for everything else
    // roster linkage is primarily about audience
    accessLabel() {
      return this.isPractice ? 'Attendance & check-in access' : 'Who it\'s for';
    },
    // A color picked via the native input (i.e. not one of the preset swatches).
    // Hex comparison is case-insensitive: saved colors and the native picker
    // don't agree on casing with the preset list.
    customColorActive() {
      const vm = this;
      return !!vm.eventColor && !vm.colorOptions.some((c) => vm.isActiveColor(c));
    },
    // States the schedule back in the coach's own mental model ("Mondays 5-7
    // until July 20") so the repeat-until vs end-date distinction is explicit
    repeatSummary() {
      const vm = this;
      if (!vm.repeatable || !vm.anyRepeatDays) {
        return null;
      }
      const days = vm.weekdayOptions.filter((d) => vm.repeatDays[d.key]).map((d) => d.long).join(' & ');
      const parts = [`Repeats every ${days}`];
      if (vm.allDay) {
        parts.push('all day');
      } else if (vm.startTimeStr && vm.endTimeStr) {
        parts.push(`${moment(vm.startTimeStr, 'HH:mm').format('h:mm a')}–${moment(vm.endTimeStr, 'HH:mm').format('h:mm a')}`);
      }
      parts.push(vm.repeatUntil ? `until ${moment(vm.repeatUntil, 'YYYY-MM-DD').format('MMM D, YYYY')}` : 'until…');
      return parts.join(' · ');
    },
    notDestroyedRosterEvents() {
      return _.flatMap(this.rosterEvents).filter((re) => !re._destroy);
    },
    readableEventType() {
      const labels = {
        practice: 'Practice',
        dual_meet: 'Dual Meet',
        scramble: 'Scramble',
        tournament: 'Tournament',
        other: 'Other',
      };
      return labels[this.eventType] || this.eventType;
    },
    // Same practice-vs-everything-else tone split as check_in_access.vue:
    // practices talk about the check-in gate, other types talk about audience
    templateAccessSummary() {
      const vm = this;
      if (vm.checkInMode === 'rosters' && vm.notDestroyedRosterEvents.length > 0) {
        const linked = vm.notDestroyedRosterEvents;
        const names = linked.map((re) => re.roster.name).join(', ');
        const counts = linked.map((re) => re.roster.wrestler_count).filter((c) => c != null);
        const haveCounts = counts.length === linked.length;
        const total = counts.reduce((sum, c) => sum + c, 0);
        if (vm.isPractice) {
          return haveCounts
            ? { cssClass: 'text-green-700', message: `✓ ${total} wrestlers (${names}) can check in.` }
            : { cssClass: 'text-green-700', message: `✓ Only wrestlers on ${names} can check in.` };
        }
        return haveCounts
          ? { cssClass: 'text-green-700', message: `✓ For ${names} — ${total} wrestlers. They'll see it as theirs; check-in follows.` }
          : { cssClass: 'text-green-700', message: `✓ For ${names}. They'll see it as theirs; check-in follows.` };
      }
      if (vm.checkInMode === 'rosters') {
        if (vm.isPractice) {
          return { cssClass: 'text-yellow-700', message: 'Pick at least one roster — until you do, anyone could check in.' };
        }
        return { cssClass: 'text-gray-600', message: 'No rosters linked — the whole team will see this event.' };
      }
      if (vm.isPractice) {
        return { cssClass: 'text-yellow-700', message: '⚠ Any wrestler on the team can check in to this event.' };
      }
      return { cssClass: 'text-gray-600', message: 'Everyone on the team will see this event and can check in.' };
    },
    reviewWhen() {
      if (!this.eventStart || !this.eventEnd) {
        return '';
      }
      let when;
      if (this.allDay) {
        // eventEnd is exclusive for all-day spans
        const endDisplay = moment(this.eventEnd).subtract(1, 'day');
        when = this.eventStart.isSame(endDisplay, 'day')
          ? `All day ${this.eventStart.format('ddd MMM D')}`
          : `All day ${this.eventStart.format('ddd MMM D')} – ${endDisplay.format('ddd MMM D')}`;
      } else {
        when = `${this.eventStart.format('ddd MMM D')} · ${this.eventStart.format('h:mm a')}–${this.eventEnd.format('h:mm a')}`;
        if (differsFromBrowser(formTimeZone(this.eventFormZone), this.eventStart)) {
          when += ` ${this.eventStart.format('z')}`;
        }
      }
      return when;
    },
    // Whether the review should surface the Repeat/Until subheads. The No toggle
    // clears repeatDays, so anyRepeatDays already reflects the enabled state.
    isRepeating() {
      return this.repeatable && this.anyRepeatDays;
    },
    // A repeat-until that lands on or before the event's start date yields a
    // series that ends before it begins. Only flag once there's a valid start
    // and a chosen until — the empty-until case is handled by its own gate.
    repeatUntilInvalid() {
      const vm = this;
      if (!vm.isRepeating || !vm.repeatUntil || !vm.eventStart) {
        return false;
      }
      return !moment(vm.repeatUntil, 'YYYY-MM-DD').isAfter(vm.eventStart, 'day');
    },
    // The date the repeating series stops, in the review's tone
    reviewRepeatUntil() {
      if (!this.repeatUntil) {
        return '';
      }
      return moment(this.repeatUntil, 'YYYY-MM-DD').format('MMM D, YYYY');
    },
    reviewAccess() {
      return this.templateAccessSummary;
    },
    // Location as it will be saved: a structured location's name, or the
    // free-text "Other..." value. Null when the coach left it blank, so the
    // review can show a muted "No location set".
    reviewLocation() {
      if (this.payloadLocation && this.payloadLocation.trim()) {
        return this.payloadLocation.trim();
      }
      if (this.payloadLocationName && this.payloadLocationName.trim()) {
        return this.payloadLocationName.trim();
      }
      return null;
    },
    hasReviewNotes() {
      return plainTextFromTrix(this.reviewNotesHtml).length > 0;
    },
    // Human-readable summary of a calendar slot passed in the URL (drag-select
    // or "+" button). Null when the wizard was opened directly, so the home
    // step only surfaces the hint when there's actually a slot to carry over —
    // it applies whether they create fresh or reuse a template.
    calendarSlotHint() {
      const cal = this.calendarPrefill();
      if (!cal.startDate) {
        return null;
      }
      let when = moment(cal.startDate, 'YYYY-MM-DD').format('ddd, MMM D');
      if (cal.allDay) {
        when += ' · All day';
      } else if (cal.startTime) {
        const start = moment(cal.startTime, 'HH:mm').format('h:mm a');
        const end = cal.endTime ? moment(cal.endTime, 'HH:mm').format('h:mm a') : null;
        when += end ? ` · ${start}–${end}` : ` · ${start}`;
      }
      return when;
    },
  },
  created() {
    const vm = this;
    vm.applyQueryParamPrefill();
    vm.getTemplates();
    vm.getPaidSessions();
    vm.applyFromEventPrefill();
  },
  mounted() {
    document.addEventListener('trix-initialize', this.trixInit);
    // Browser back/forward walks the wizard steps instead of leaving the page
    // (web only — in the app embed the native nav stack owns back, and a
    // webview back gesture pops the whole screen rather than firing popstate).
    if (!this.forAppEmbed) {
      history.replaceState({ wizardStep: this.step }, '');
      window.addEventListener('popstate', this.popStateHandler);
    }
    // In the embed, a native back press mid-wizard walks the steps like
    // browser back does on the web; from home the exit guard is off, so the
    // native pop proceeds and leaves the wizard as expected.
    this.listenForNativeBack(this.nativeBackRequested);
  },
  destroyed() {
    document.removeEventListener('trix-initialize', this.trixInit);
    if (!this.forAppEmbed) {
      window.removeEventListener('popstate', this.popStateHandler);
    }
    this.stopListeningForNativeBack();
  },
  watch: {
    eventType(newEventType) {
      const vm = this;
      // Auto-pick a color per type (same mapping the old form used)
      if (newEventType === 'practice') {
        vm.eventColor = '#27aa83';
      } else if (newEventType === 'dual_meet' || newEventType === 'scramble') {
        vm.eventColor = '#4299d7';
      } else if (newEventType === 'tournament') {
        vm.eventColor = '#663dae';
      } else if (newEventType === 'other') {
        vm.eventColor = '#c8992d';
      }

      // Practices must restrict check-in to rosters
      if (newEventType === 'practice' && vm.checkInMode === 'anyone') {
        vm.checkInMode = 'rosters';
      }

      // Clear a stale opposing-team selection when navigating away from dual
      // meet — the picker UI hides but the state would otherwise persist into
      // buildParams() and leak opposing_team_id onto a non-dual-meet event.
      if (newEventType !== 'dual_meet') {
        vm.opposingTeam = null;
      }
    },
    step(newStep) {
      this.error = false;
      this.errorMessage = '';
      // Guard the native back button/swipe while the user has wizard progress
      // to lose; the home screen has none. A successful create pops via the
      // bridge's pop message, which bypasses the guard by design.
      this.setNativeExitConfirmation(newStep !== 'home', 'Discard this event?');
    },
  },
  methods: {
    // Raw step setter — used by the forward/back helpers and by popstate. Does
    // not touch history itself, so it's safe to drive from the popstate handler.
    goTo(step) {
      // Snapshot notes off the live trix editor whenever we enter review — covers
      // the Next button and browser back/forward (popstate) landing on step 4
      if (step === '4') {
        this.reviewNotesHtml = $('#wizard-notes').val() || '';
      }
      this.step = step;
      window.scrollTo({ top: 0 });
    },
    // Forward navigation: change the step and push a history entry so the
    // browser back button returns to the step we came from (web only; the
    // embed records the same thing in stepHistory).
    advanceTo(step) {
      if (this.forAppEmbed) {
        this.stepHistory.push(this.step);
      }
      this.goTo(step);
      if (!this.forAppEmbed) {
        history.pushState({ wizardStep: step }, '');
      }
    },
    // Backward navigation: on the web let the browser pop history, which fires
    // popStateHandler and restores the prior step. In the embed pop our own
    // step history; the linear fallback only matters when it's empty.
    goBack(fallbackStep) {
      if (this.forAppEmbed) {
        this.goTo(this.stepHistory.pop() || fallbackStep);
      } else {
        history.back();
      }
    },
    popStateHandler(event) {
      if (event.state && 'wizardStep' in event.state) {
        this.goTo(event.state.wizardStep);
      }
    },
    nativeBackRequested() {
      if (this.onNumberedStep) {
        this.goBack(this.previousStep);
      }
    },
    startFreshEvent() {
      this.resetForm();
      this.advanceTo('1');
    },
    // Both entry points (fresh build, template pick) start from a clean slate —
    // otherwise state from a previously viewed template (rosters, notes, color,
    // paid session...) leaks into the next flow after backing out to home.
    resetForm() {
      const vm = this;
      vm.selectedTemplate = null;
      vm.cameFromTemplate = false;
      vm.eventName = '';
      vm.eventType = 'practice';
      vm.eventColor = '#27aa83';
      vm.startDateStr = '';
      vm.startTimeStr = '';
      vm.endDateStr = '';
      vm.endTimeStr = '';
      vm.allDay = false;
      vm.eventStart = null;
      vm.eventEnd = null;
      vm.dateRangeValid = false;
      vm.repeatEnabled = false;
      vm.weekdayOptions.forEach((day) => {
        vm.repeatDays[day.key] = false;
      });
      vm.repeatUntil = '';
      vm.checkInMode = 'rosters';
      vm.rosterEvents = {};
      vm.locationId = null;
      vm.locationFreeText = '';
      vm.payloadLocationId = null;
      vm.payloadLocation = '';
      vm.payloadLocationName = '';
      vm.eventTimeZone = null;
      vm.timeZoneOverride = null;
      vm.zoneResolvedForLocationId = null;
      vm.paidSession = null;
      vm.pendingPaidSessionId = null;
      vm.eventNotes = '';
      if (vm.trixEditor) {
        vm.trixEditor.loadHTML('');
      }
      vm.opposingTeam = null;
      vm.error = false;
      vm.errorMessage = '';
      // Restore any calendar drag prefill, then re-mount the date fields so
      // the reset values take
      vm.applyQueryParamPrefill();
      vm.formStateKey += 1;
    },
    // ---------- prefill from the calendar (drag-select / + button) ----------
    // Parsed calendar prefill (drag-select / "+" button) from the URL. Any
    // field the calendar didn't pass comes back null/false.
    calendarPrefill() {
      const params = new URLSearchParams(window.location.search);
      return {
        startDate: params.get('start_date'),
        endDate: params.get('end_date'),
        startTime: params.get('start_time'),
        endTime: params.get('end_time'),
        allDay: params.get('all_day') === 'true',
      };
    },
    applyQueryParamPrefill() {
      const vm = this;
      const cal = vm.calendarPrefill();
      if (cal.startDate) {
        vm.startDateStr = cal.startDate;
        vm.endDateStr = cal.endDate || cal.startDate;
      }
      if (cal.startTime) {
        vm.startTimeStr = cal.startTime;
        vm.endTimeStr = cal.endTime || cal.startTime;
      }
      if (cal.allDay) {
        vm.allDay = true;
      }
    },
    // ---------- duplicate an existing event ----------
    // /events/new?from_event_id=123 (the "Duplicate" button on an event page)
    // loads that one event as a template and drops the user straight into
    // step 1, exactly as if they had tapped it on the recent-events list.
    applyFromEventPrefill() {
      const vm = this;
      const fromEventId = new URLSearchParams(window.location.search).get('from_event_id');
      if (!fromEventId) {
        return;
      }
      vm.loading = true;
      axios.get(vm.$apiService.eventTemplateUrl(fromEventId))
        .then((response) => {
          vm.loading = false;
          vm.useTemplate(response.data.template);
        })
        .catch(() => {
          // Non-fatal: fall back to the plain home screen with a hint
          vm.loading = false;
          vm.error = true;
          vm.errorMessage = "We couldn't load that event to duplicate it. Start fresh below or pick a recent event.";
        });
    },
    // ---------- templates ----------
    getTemplates() {
      const vm = this;
      axios.get(vm.$apiService.recentEventTemplatesUrl())
        .then((response) => {
          vm.templates = response.data.templates;
        })
        .catch(() => {
          // Non-fatal: the home screen simply shows the "New event" button
          vm.templates = [];
        });
    },
    templateTypeLabel(tpl) {
      const labels = {
        practice: 'Practice', dual_meet: 'Dual Meet', scramble: 'Scramble', tournament: 'Tournament', other: 'Other',
      };
      return labels[tpl.event_type] || tpl.event_type;
    },
    templateRosterSummary(tpl) {
      const names = tpl.rosters.map((r) => r.name).join(', ');
      const total = tpl.rosters.reduce((sum, r) => sum + (r.wrestler_count || 0), 0);
      return `${names} (${total})`;
    },
    useTemplate(tpl) {
      const vm = this;
      // Clean slate so nothing lingers from a previously viewed template or an
      // abandoned fresh build
      vm.resetForm();
      vm.selectedTemplate = tpl;
      vm.cameFromTemplate = true;

      vm.eventName = tpl.name;
      vm.eventType = tpl.event_type;
      // Set after the eventType watcher so the template's saved color wins
      vm.$nextTick(() => {
        if (tpl.color) {
          vm.eventColor = tpl.color;
        }
      });

      // Date & times are normally blanked: a reused event almost always needs a
      // fresh "when", so we force an active choice (the step-1 gate blocks Next
      // until the range is valid). The exception is arriving from a calendar
      // slot selection — that IS an explicit when, so honor whatever the
      // calendar passed and only blank the fields it didn't supply.
      const cal = vm.calendarPrefill();
      vm.startDateStr = cal.startDate || '';
      vm.endDateStr = cal.endDate || cal.startDate || '';
      vm.startTimeStr = cal.startTime || '';
      vm.endTimeStr = cal.endTime || cal.startTime || '';
      // Explicit calendar intent wins over the template's saved all-day-ness: a
      // timed slot forces timed, an all-day drag forces all-day.
      if (cal.startTime) {
        vm.allDay = false;
      } else if (cal.allDay) {
        vm.allDay = true;
      } else {
        vm.allDay = tpl.all_day;
      }
      vm.repeatUntil = '';

      // Repeat pattern carried into the step-1 pills; reveal them if the
      // template actually repeats so the carried-over days are visible.
      vm.weekdayOptions.forEach((day) => {
        vm.repeatDays[day.key] = tpl.weekdays.includes(day.wday);
      });
      vm.repeatEnabled = vm.anyRepeatDays;

      // Check-in access carried over
      vm.rosterEvents = {};
      tpl.rosters.forEach((roster) => {
        vm.$set(vm.rosterEvents, roster.id, {
          _destroy: false,
          roster: {
            id: roster.id,
            name: roster.name,
            wrestler_count: roster.wrestler_count,
          },
        });
      });
      vm.checkInMode = (tpl.rosters.length > 0 || tpl.event_type === 'practice') ? 'rosters' : 'anyone';

      // Extras carried over
      vm.locationId = tpl.location_id;
      vm.locationFreeText = tpl.location || '';
      vm.payloadLocationId = tpl.location_id;
      vm.payloadLocation = tpl.location || '';
      vm.timeZoneOverride = tpl.time_zone_override || null;
      vm.pendingPaidSessionId = tpl.paid_session_id;
      vm.matchPendingPaidSession();
      vm.eventNotes = tpl.notes || '';
      if (vm.trixEditor) {
        vm.trixEditor.loadHTML(vm.eventNotes);
      }

      vm.advanceTo('1');
    },
    // ---------- step state handlers ----------
    dateRangeChanged({
      start, end, allDay, valid,
    }) {
      const vm = this;
      vm.eventStart = start;
      vm.eventEnd = end;
      vm.allDay = allDay;
      vm.dateRangeValid = valid;
      // Keep the raw strings in sync so re-mounts restore the same values
      if (start) {
        vm.startDateStr = start.format('YYYY-MM-DD');
        if (!allDay) {
          vm.startTimeStr = start.format('HH:mm');
        }
      }
      if (end) {
        const endForDisplay = allDay ? moment(end).subtract(1, 'day') : end;
        vm.endDateStr = endForDisplay.format('YYYY-MM-DD');
        if (!allDay) {
          vm.endTimeStr = end.format('HH:mm');
        }
      }
    },
    toggleWeekday(key) {
      this.repeatDays[key] = !this.repeatDays[key];
    },
    setRepeatEnabled(enabled) {
      const vm = this;
      vm.repeatEnabled = enabled;
      // Turning repeat off wipes the picked days and end date so downstream
      // logic (summary, review, buildParams) reads it as a one-off event.
      if (!enabled) {
        vm.weekdayOptions.forEach((day) => {
          vm.repeatDays[day.key] = false;
        });
        vm.repeatUntil = '';
      }
    },
    setCheckInMode(mode) {
      this.checkInMode = mode;
    },
    toggleRoster(roster) {
      const vm = this;
      // Clear the search results after a pick
      vm.$notificationManager.emit('profile-clicked', 'Roster');
      if (vm.rosterEvents[roster.id]) {
        vm.$delete(vm.rosterEvents, roster.id);
        return;
      }

      vm.$set(vm.rosterEvents, roster.id, {
        _destroy: false,
        roster: {
          id: roster.id,
          name: roster.name,
          wrestler_count: _.get(roster, 'stats.roster_memberships_count', _.get(roster, 'wrestler_count', null)),
        },
      });
    },
    locationChanged({
      locationId, location, locationName, timeZone, ready,
    }) {
      this.payloadLocationId = locationId;
      this.payloadLocation = location;
      this.payloadLocationName = locationName || '';
      // Step 1 re-mounts the picker, which emits before its list reloads; keep
      // the known zone until it can actually resolve the location's
      if (ready || !locationId) {
        this.eventTimeZone = timeZone || null;
        this.zoneResolvedForLocationId = locationId;
      }
      // Also mirror into the initial* props: step 1 is v-if'd, so the picker
      // re-mounts on every visit and restores from these
      this.locationId = locationId;
      this.locationFreeText = location;
    },
    isActiveColor(color) {
      return !!this.eventColor && this.eventColor.toLowerCase() === color.toLowerCase();
    },
    // ---------- trix ----------
    trixInit(event) {
      const { editor } = event.target;
      this.trixEditor = editor;
      if (this.eventNotes) {
        this.trixEditor.loadHTML(this.eventNotes);
      }
    },
    // ---------- paid sessions ----------
    getPaidSessions() {
      const vm = this;
      if (!vm.paidSessionTagging) {
        return;
      }

      const url = vm.$apiService.futureSessionsUrl();
      vm.$apiService.loadAllPages(url, 'paid_sessions')
        .then((sessions) => {
          vm.paidSessions = sessions;
          vm.matchPendingPaidSession();
        })
        .catch(() => {
          vm.paidSessions = [];
        });
    },
    matchPendingPaidSession() {
      const vm = this;
      if (vm.pendingPaidSessionId == null) {
        return;
      }
      const match = vm.paidSessions.find((ps) => ps.id === vm.pendingPaidSessionId);
      if (match) {
        vm.paidSession = match;
      }
    },
    // ---------- validation & submit ----------
    switchToPractice() {
      this.eventType = 'practice';
    },
    nextFromBasics() {
      const vm = this;
      const missing = [];
      if (!vm.eventName) {
        missing.push('name');
      }
      if (!vm.dateRangeValid) {
        missing.push('date & time');
      }
      if (vm.eventType === 'dual_meet' && !vm.opposingTeam) {
        missing.push('opposing team');
      }
      if (vm.repeatable && vm.anyRepeatDays && !vm.repeatUntil) {
        missing.push('repeat until date');
      }
      if (missing.length > 0) {
        vm.error = true;
        vm.errorMessage = `Please fill in: ${missing.join(', ')}.`;
        return;
      }
      if (vm.repeatUntilInvalid) {
        vm.error = true;
        vm.errorMessage = 'The repeat-until date must be after the start date.';
        return;
      }
      if (vm.zoneUnknown) {
        vm.error = true;
        vm.errorMessage = "We don't know the location's time zone yet. Wait a moment, or refresh the page.";
        return;
      }
      vm.advanceTo('2');
    },
    nextFromCheckIn() {
      const vm = this;
      if (vm.eventType === 'practice' && vm.notDestroyedRosterEvents.length === 0) {
        vm.error = true;
        vm.errorMessage = 'Practices must be linked to at least one roster. Pick one above, or go back and switch the event type to Other.';
        return;
      }
      vm.advanceTo('3');
    },
    validateForCreate() {
      const vm = this;
      if (!vm.eventName || !vm.eventType) {
        vm.error = true;
        vm.errorMessage = 'Please make sure the event has a name and type.';
        return false;
      }
      if (!vm.dateRangeValid) {
        vm.error = true;
        vm.errorMessage = 'Please double check the date & time.';
        return false;
      }
      if (vm.zoneUnknown) {
        vm.error = true;
        vm.errorMessage = "We don't know the location's time zone yet. Wait a moment, or refresh the page.";
        return false;
      }
      if (vm.eventType === 'practice' && (vm.checkInMode !== 'rosters' || vm.notDestroyedRosterEvents.length === 0)) {
        vm.error = true;
        vm.errorMessage = 'Practices must be linked to at least one roster.';
        return false;
      }
      if (vm.eventType === 'dual_meet' && !vm.opposingTeam) {
        vm.error = true;
        vm.errorMessage = 'Dual meets need an opposing team.';
        return false;
      }
      return true;
    },
    buildParams() {
      const vm = this;
      const rosterEventsAttributes = vm.checkInMode === 'rosters'
        ? _.flatMap(vm.rosterEvents).filter((re) => !re._destroy).map((re) => ({ roster_id: re.roster.id, _destroy: false }))
        : [];

      const params = {
        event: {
          name: vm.eventName,
          event_type: vm.eventType,
          color: vm.eventColor,
          start_at: vm.eventStart.toISOString(),
          end_at: vm.eventEnd.toISOString(),
          all_day: vm.allDay,
          location: vm.payloadLocation,
          location_id: vm.payloadLocationId,
          // All-day dates are zone-less; a hidden override would only skew
          // which weekday repeats land on
          time_zone_override: vm.allDay ? null : vm.timeZoneOverride,
          notes: $('#wizard-notes').val(),
          roster_events_attributes: rosterEventsAttributes,
        },
      };

      const repeating = vm.repeatable && vm.anyRepeatDays && vm.repeatUntil;
      if (repeating) {
        const tz = formTimeZone(vm.eventFormZone);
        // Normalize to end-of-day so UTC offsets can't drop the final occurrence
        const untilString = moment.tz(vm.repeatUntil, 'YYYY-MM-DD', tz)
          .endOf('day')
          .utc()
          .toISOString();
        params.event.repeat = {
          until: untilString,
          sun: vm.repeatDays.sun,
          mon: vm.repeatDays.mon,
          tue: vm.repeatDays.tue,
          wed: vm.repeatDays.wed,
          thu: vm.repeatDays.thu,
          fri: vm.repeatDays.fri,
          sat: vm.repeatDays.sat,
        };
      }

      if (vm.eventType === 'dual_meet' && vm.opposingTeam) {
        params.event.opposing_team_id = vm.opposingTeam.id;
      }

      if (vm.paidSession) {
        params.event.paid_session_id = vm.paidSession.id;
      }
      return params;
    },
    postEvent: _.throttle(function () {
      const vm = this;
      if (!vm.validateForCreate()) {
        return;
      }
      if (vm.loading === true) {
        return;
      }
      vm.loading = true;
      vm.error = false;

      const url = vm.$apiService.eventsUrl('');
      axios.post(url, vm.buildParams())
        .then((response) => {
          const event = response.data;
          vm.$notificationManager.emit('show-toast', `${event.name} created`, true);
          if (vm.forAppEmbed) {
            vm.loading = false;
            // Tells the native app the event was created, to pop to the root,
            // and to reload the calendar
            vm.askAppToPop({
              name: 'event_saved',
              data: {
                event_id: event.id,
              },
            });
          } else {
            // Land the coach on the calendar at the event's date. Keep loading
            // spinning through the redirect.
            const eventDate = moment(event.start_at);
            window.location.assign(`/calendar#year=${eventDate.year()}&month=${eventDate.month() + 1}&day=${eventDate.date()}&view=dayGridMonth`);
          }
        })
        .catch((error) => {
          vm.loading = false;
          vm.error = true;
          vm.errorMessage = vm.serverErrorMessage(error);
          vm.notifyAppOfValidationError(vm.errorMessage);
        });
    }, 500),
    serverErrorMessage(error) {
      const errors = _.get(error, 'response.data.errors', null);
      if (errors) {
        // errors is an ActiveModel errors hash, e.g. { base: ["message"] }
        const messages = _.flatMap(_.values(errors));
        if (messages.length > 0) {
          return messages.join(' ');
        }
      }
      return 'Error, please try again later or contact support.';
    },
  },
};
</script>
